import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

interface StatItem {
  target: number;
  suffix: string;
  label: string;
  prefix?: string;
}

const stats: StatItem[] = [
  { target: 150, suffix: '+', label: '完成案例' },
  { target: 12, suffix: '+', label: '年設計經驗' },
  { target: 98, suffix: '%', label: '業主滿意度' },
  { target: 30000, suffix: '+', label: '規劃坪數', prefix: '' },
];

function useCountUp(target: number, duration = 1800, started = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!started) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);

  return count;
}

function AnimatedStat({ item, started }: { item: StatItem; started: boolean }) {
  const count = useCountUp(item.target, 1800, started);

  const display =
    item.target >= 10000
      ? `${(count / 1000).toFixed(count >= item.target ? 0 : 1)}K`
      : count.toString();

  return (
    <div>
      <div
        className="text-white font-bold tabular-nums"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2.25rem', lineHeight: 1.1 }}
      >
        {item.prefix ?? ''}{display}{item.suffix}
      </div>
      <div className="text-white/60 text-sm mt-1">{item.label}</div>
    </div>
  );
}

function useHeroParallax(speed = 0.35) {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setOffset(window.scrollY * speed);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return offset;
}

export default function HeroSection() {
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsStarted, setStatsStarted] = useState(false);
  const parallaxOffset = useHeroParallax(0.35);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !statsStarted) {
          setStatsStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [statsStarted]);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Background Image — parallax */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=luxurious%20modern%20commercial%20interior%20design%20space%20high-end%20restaurant%20or%20retail%20store%20with%20warm%20lighting%2C%20natural%20stone%20walls%2C%20wood%20elements%2C%20elegant%20minimalist%20atmosphere%2C%20professional%20commercial%20space%20design%2C%20neutral%20tones%20beige%20gray%20warm%20white&width=1920&height=1080&seq=hero001&orientation=landscape"
          alt="心旅商研商業空間設計"
          className="w-full object-cover object-top"
          style={{
            height: '115%',
            transform: `translateY(${parallaxOffset}px)`,
            willChange: 'transform',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/30"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col justify-center max-w-[1440px] mx-auto w-full px-6 md:px-10 pt-32 pb-20">
        <div className="max-w-3xl">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-px bg-[#b8956a]"></div>
            <span className="text-[#b8956a] text-sm font-medium tracking-[0.2em] uppercase">Commercial Space Design</span>
          </div>

          {/* H1 */}
          <h1
            className="text-white font-bold leading-[1.25] mb-6"
            style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)' }}
          >
            商業空間，<br />
            是展現品牌格調最有力的
            <em
              className="not-italic text-[#d4b896]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontStyle: 'italic' }}
            >
              無聲語言
            </em>
          </h1>

          {/* Subtitle */}
          <p
            className="text-white/75 leading-[1.75] mb-10 max-w-xl"
            style={{ fontSize: '1.1875rem' }}
          >
            心旅商研整合商業策略規劃、空間設計與工程落地，
            讓每一個商業空間不只好看，更能創造品牌價值、提升消費體驗、帶動實際營收。
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#b8956a] text-white font-semibold rounded-full whitespace-nowrap hover:bg-[#a07d58] transition-colors duration-300 cursor-pointer text-[1rem]"
            >
              <i className="ri-calendar-check-line"></i>
              預約免費諮詢
            </Link>
            <Link
              to="/cases"
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/50 text-white font-medium rounded-full whitespace-nowrap hover:bg-white/10 transition-colors duration-300 cursor-pointer text-[1rem]"
            >
              <i className="ri-gallery-line"></i>
              查看精選案例
            </Link>
          </div>

          {/* Stats with counter animation */}
          <div
            ref={statsRef}
            className="flex flex-wrap gap-10 mt-16 pt-12 border-t border-white/20"
          >
            {stats.map((s) => (
              <AnimatedStat key={s.label} item={s} started={statsStarted} />
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="relative z-10 flex justify-center pb-10">
        <div className="flex flex-col items-center gap-2 text-white/50">
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <i className="ri-arrow-down-line animate-bounce"></i>
        </div>
      </div>
    </section>
  );
}
